import { BALANCE } from '@2k48/shared';

/**
 * The numbers the debug overlay shows.
 *
 * The point of collecting all of these together is diagnosis, not vanity: when
 * the game feels bad, "is this the network or the renderer?" has to be
 * answerable in one glance. A frame time of 40ms with 2 patches/second is a
 * render problem; 8ms frames with an RTT of 300ms is not.
 */

export interface MetricsSnapshot {
  fps: number;
  frameMs: number;
  worstFrameMs: number;
  /** Frames over the 16ms budget in the last second. */
  droppedFrames: number;
  /** Nodes currently inside the game area. */
  nodeCount: number;
  /** Colyseus state patches received per second. */
  patchesPerSecond: number;
  /** Server clock minus client clock, in ms. */
  clockOffsetMs: number;
  clockRttMs: number;
  clockJitterMs: number;
  poolFree: number;
  poolBusy: number;
  /** Effects declined because the concurrency budget was spent. */
  effectsRefused: number;
}

class Metrics {
  private frames = 0;
  private frameTimeSum = 0;
  private windowStart = 0;
  private patches = 0;

  private snapshot: MetricsSnapshot = {
    fps: 0,
    frameMs: 0,
    worstFrameMs: 0,
    droppedFrames: 0,
    nodeCount: 0,
    patchesPerSecond: 0,
    clockOffsetMs: 0,
    clockRttMs: 0,
    clockJitterMs: 0,
    poolFree: 0,
    poolBusy: 0,
    effectsRefused: 0,
  };

  private worstThisWindow = 0;
  private droppedThisWindow = 0;

  /** Called from the single render loop, once per frame. */
  recordFrame(elapsedMs: number, deltaMs: number): void {
    if (this.windowStart === 0) this.windowStart = elapsedMs;

    this.frames += 1;
    this.frameTimeSum += deltaMs;
    if (deltaMs > this.worstThisWindow) this.worstThisWindow = deltaMs;
    // A *dropped* frame is one that missed its refresh, not one that merely
    // took longer than our internal work budget. At a flawless 60fps every
    // interval is 16.7ms, so counting those would make this read 60/60.
    if (deltaMs > BALANCE.perf.heavyFrameBudgetMs) this.droppedThisWindow += 1;

    const windowMs = elapsedMs - this.windowStart;
    if (windowMs < 500) return;

    this.snapshot = {
      ...this.snapshot,
      fps: Math.round((this.frames * 1000) / windowMs),
      frameMs: Math.round((this.frameTimeSum / this.frames) * 10) / 10,
      worstFrameMs: Math.round(this.worstThisWindow * 10) / 10,
      droppedFrames: this.droppedThisWindow,
      patchesPerSecond: Math.round((this.patches * 1000) / windowMs),
    };

    this.frames = 0;
    this.frameTimeSum = 0;
    this.worstThisWindow = 0;
    this.droppedThisWindow = 0;
    this.patches = 0;
    this.windowStart = elapsedMs;
  }

  recordPatch(): void {
    this.patches += 1;
  }

  setNodeCount(count: number): void {
    this.snapshot.nodeCount = count;
  }

  /** Combat effect pool occupancy. */
  setPool(free: number, busy: number): void {
    this.snapshot.poolFree = free;
    this.snapshot.poolBusy = busy;
  }

  /**
   * Board effects declined for want of budget. Owned by `EffectsLayer`, which
   * is a different pool from the combat one above — sharing `setPool` would let
   * each reset the other's reading.
   */
  setEffectsRefused(count: number): void {
    this.snapshot.effectsRefused = count;
  }

  setClock(offsetMs: number, rttMs: number, jitterMs: number): void {
    this.snapshot.clockOffsetMs = Math.round(offsetMs);
    this.snapshot.clockRttMs = Math.round(rttMs);
    this.snapshot.clockJitterMs = Math.round(jitterMs);
  }

  read(): MetricsSnapshot {
    return this.snapshot;
  }
}

export const metrics = new Metrics();

/** `?debug=1` in the URL. Checked once; flipping it means a reload. */
export const DEBUG_ENABLED: boolean =
  typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('debug') === '1';
